<template>
  <div class="w-full border rounded-md p-3">
    <!-- Selected Tags -->
    <div class="min-h-12 max-h-32 overflow-y-auto mb-3">
      <div class="flex flex-wrap gap-2">
        <div
          v-for="tag in sortedSelected"
          :key="tag"
          class="flex items-center px-2 py-1 border rounded-md text-sm bg-gray-100"
          :class="{ 'opacity-50 pointer-events-none': disabled }"
          @click="removeTag(tag)"
        >
          <span>{{ tag }}</span>
          <button
            v-if="!disabled"
            type="button"
            class="ml-1 text-gray-500"
            @click="removeTag(tag)"
          >
            x
          </button>
        </div>
      </div>
    </div>
    <hr class="my-2 border-t border-gray-300">
    <div class="mb-2">
      <tx-input
        v-model="searchText" clearable faicon="fa-light fa-magnifying-glass" class="pb-1"
        :placeholder="t('general.search')"
      />
    </div>
    <!-- Available Tags -->
    <div class="max-h-40 overflow-y-auto pr-1">
      <div class="flex flex-wrap gap-2">
        <div
          v-for="tag in filteredAvailable"
          :key="tag"
          class="cursor-pointer px-2 py-1 border rounded-md text-sm hover:bg-gray-100"
          :class="{ 'opacity-50 pointer-events-none': disabled }"
          @click="!disabled && selectTag(tag)"
        >
          {{ tag }}
        </div>
      </div>
    </div>
    <div v-if="hasError" class="mt-2">
      <p v-for="error in errors" :key="error.$uid" class="text-xs italic text-red-500">
        {{ error.$message }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { ErrorObject } from '@vuelidate/core'
import { useI18n } from 'vue-i18n'
import TxInput from './TxInput.vue'

interface Props {
  modelValue: string[]
  data: string[]
  disabled?: boolean
  required?: boolean
  clearable?: boolean
  sortList?: boolean
  errors?: ErrorObject[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  data: () => [],
  disabled: false,
  required: false,
  clearable: false,
  sortList: true,
  errors: () => [],
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
  (e: 'change', value: string[]): void
}>()

const { t } = useI18n()

const searchText = ref('')

const hasError = computed(() => props.errors.length > 0)

const sortedSelected = computed(() => {
  return props.sortList ? [...props.modelValue].sort() : props.modelValue
})

const sortedAvailable = computed(() => {
  const available = props.data.filter(tag => !props.modelValue.includes(tag))
  return props.sortList ? available.sort() : available
})

const filteredAvailable = computed(() => {
  if (!searchText.value.trim()) { return sortedAvailable.value }
  return sortedAvailable.value.filter(tag =>
    tag.toLowerCase().includes(searchText.value.toLowerCase()),
  )
})

function selectTag(tag: string) {
  if (props.modelValue.includes(tag)) {
    removeTag(tag)
  }
  else {
    const newVal = [...props.modelValue, tag]
    update(newVal)
  }
}

function removeTag(tag: string) {
  const newVal = props.modelValue.filter(t => t !== tag)
  update(newVal)
}

function update(val: string[]) {
  emit('update:modelValue', val)
  emit('change', val)
}
</script>
